import React from 'react';
import {
  FlexiblePageTreeNode,
  FlexiblePageTreeNodeType,
  SectionTreeNode,
  ComponentTreeNode,
  isTabGroupTreeNode,
  isTabListTreeNode,
  isTabPanelsTreeNode,
  isButtonTreeNode,
  isStructuredButtonTreeNode,
  isDisclosureContentTreeNode,
  isSlideDeckRenderMode,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import { Button } from './Button';
import { Container } from './Container';
import { Section } from './Section';
import { Icon } from './Icon';
import { Image } from './Image';
import { Svg } from './Svg';
import { Text } from './Text';
import { Video } from './Video';
import { Embed } from './Embed';
import { Dialog } from './Dialog';
import { TabGroup } from './TabGroup';
import { TabList } from './TabList';
import { TabPanels } from './TabPanels';
import { Disclosure, DisclosureContent } from './Disclosure';
import { Page } from './Page';
import { SlideDeckHarness } from './SlideDeckHarness';
import { SlideFrame } from './SlideFrame';
import { stripLinksFromTreeNode } from '../utils/stripLinksFromTreeNode';
import { CustomComponent, splitCustomComponentData } from '../../customComponents/CustomComponent';

interface ReadonlyComponentRendererProps {
  componentTree: FlexiblePageTreeNode;
  overrideVariables?: Record<string, string>;
  staticAssetsBaseURL?: string | null;
  renderStage?: RenderStage;
  children?: React.ReactNode;
  skipFontLoading?: boolean;
  /** When true, transforms viewport breakpoint classes (lg:) to container query classes (@lg:) for container-query-based layout. Requires a @container ancestor. */
  isEditor?: boolean;
  /** When true, slide-deck pages render slides as stacked fixed-size sections instead of the interactive harness, so the PDF/PPTX export pipeline can screenshot each one. */
  isPdfExport?: boolean;
  /** When true, slide-deck pages render only the first slide scaled to fill the frame (no presentation player/chrome), so small thumbnail iframes show a legible cover instead of a letterboxed slide. */
  isThumbnail?: boolean;
}

export const ReadonlyComponentRenderer = React.memo(
  ({
    componentTree,
    overrideVariables,
    staticAssetsBaseURL,
    renderStage,
    children,
    skipFontLoading = true,
    isEditor = false,
    isPdfExport = false,
    isThumbnail = false,
  }: ReadonlyComponentRendererProps) => {
    const emptyMentionItems = React.useMemo(() => [], []);
    const emptyRefetch = React.useCallback(async () => {}, []);

    // Only pass staticAssetsBaseURL in Preview renderStage, not in Publish
    const resolvedstaticAssetsBaseURL = renderStage === RenderStage.Preview ? staticAssetsBaseURL : undefined;

    const renderComponentNode = (treeNode: ComponentTreeNode): React.ReactNode => {
      switch (treeNode.data.type) {
        case FlexiblePageTreeNodeType.Container: {
          return (
            <Container
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
              isEditor={isEditor}
            >
              {'children' in treeNode &&
                treeNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
            </Container>
          );
        }

        case FlexiblePageTreeNodeType.Text: {
          return (
            <Text
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              mentionItems={emptyMentionItems}
              refetchMentionItems={emptyRefetch}
              isEditor={false}
            />
          );
        }

        case FlexiblePageTreeNodeType.Button: {
          const buttonHasChildren = 'children' in treeNode && treeNode.children && treeNode.children.length > 0;
          return (
            <Button
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              mentionItems={emptyMentionItems}
              refetchMentionitems={emptyRefetch}
              isEditor={false}
            >
              {buttonHasChildren &&
                treeNode.children?.map((childNode: ComponentTreeNode) =>
                  renderComponentNode(stripLinksFromTreeNode(childNode)),
                )}
            </Button>
          );
        }

        case FlexiblePageTreeNodeType.StructuredButton: {
          return (
            <Button
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              mentionItems={emptyMentionItems}
              refetchMentionitems={emptyRefetch}
              isEditor={false}
            >
              {'children' in treeNode &&
                treeNode.children?.map((childNode: ComponentTreeNode) =>
                  renderComponentNode(stripLinksFromTreeNode(childNode)),
                )}
            </Button>
          );
        }

        case FlexiblePageTreeNodeType.Image: {
          return (
            <Image
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
            />
          );
        }

        case FlexiblePageTreeNodeType.Video: {
          return <Video key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
        }

        case FlexiblePageTreeNodeType.Svg: {
          return <Svg key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
        }

        case FlexiblePageTreeNodeType.Icon: {
          return <Icon key={treeNode.id} id={treeNode.id} {...treeNode.data} overrideVariables={overrideVariables} />;
        }

        case FlexiblePageTreeNodeType.Embed: {
          return (
            <Embed
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              isEditor={false}
            />
          );
        }

        case FlexiblePageTreeNodeType.Dialog: {
          return (
            <Dialog key={treeNode.id} id={treeNode.id} {...treeNode.data}>
              {'children' in treeNode &&
                treeNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
            </Dialog>
          );
        }

        case FlexiblePageTreeNodeType.TabGroup: {
          if (!isTabGroupTreeNode(treeNode) || !treeNode.children || treeNode.children.length !== 2) {
            return null;
          }
          return (
            <TabGroup
              {...treeNode.data}
              key={treeNode.id}
              id={treeNode.id}
              renderComponentNode={renderComponentNode}
              tabGroupNodes={treeNode.children ?? []}
            />
          );
        }

        case FlexiblePageTreeNodeType.TabList: {
          if (!isTabListTreeNode(treeNode)) {
            console.error('Expected TabListTreeNode');
            return null;
          }
          return (
            <TabList key={treeNode.id} id={treeNode.id} {...treeNode.data} data-no-dnd>
              {treeNode.children?.map((tab) => renderComponentNode(tab))}
            </TabList>
          );
        }

        case FlexiblePageTreeNodeType.TabPanels: {
          if (!isTabPanelsTreeNode(treeNode)) {
            console.error('Expected TabPanelsTreeNode');
            return null;
          }
          return (
            <TabPanels key={treeNode.id} id={treeNode.id} {...treeNode.data} data-no-dnd>
              {treeNode.children?.map((panel) => renderComponentNode(panel))}
            </TabPanels>
          );
        }

        case FlexiblePageTreeNodeType.Disclosure: {
          if (!treeNode.children || treeNode.children.length < 2) {
            console.error('Invalid disclosure node structure');
            return null;
          }

          const [buttonNode, disclosureContentNode] = treeNode.children;

          if (!buttonNode || !disclosureContentNode) {
            console.error('Invalid disclosure node structure');
            return null;
          }

          if (!isDisclosureContentTreeNode(disclosureContentNode)) {
            console.error('Second child must be a DisclosureContent node');
            return null;
          }

          if (!isButtonTreeNode(buttonNode) && !isStructuredButtonTreeNode(buttonNode)) {
            console.error('Disclosure trigger must be a StructuredButton node');
            return null;
          }

          let triggerElement: React.ReactNode = null;
          if (isButtonTreeNode(buttonNode)) {
            const buttonTriggerProps = {
              ...buttonNode.data,
              commandfor: disclosureContentNode.data.domTargetId,
              command: '--toggle' as const,
              className: buttonNode.data.className ? `${buttonNode.data.className} group` : 'group',
            };
            triggerElement = (
              <Button
                key={buttonNode.id}
                id={buttonNode.id}
                {...buttonTriggerProps}
                isEditor={false}
                mentionItems={emptyMentionItems}
                refetchMentionitems={emptyRefetch}
              >
                {'children' in buttonNode &&
                  buttonNode.children?.map((childNode: ComponentTreeNode) =>
                    renderComponentNode(stripLinksFromTreeNode(childNode)),
                  )}
              </Button>
            );
          } else {
            let triggerClassName = 'group';
            if (buttonNode.data.className) {
              triggerClassName = `${buttonNode.data.className} group`;
            }
            triggerElement = (
              <Button
                key={buttonNode.id}
                id={buttonNode.id}
                {...buttonNode.data}
                commandfor={disclosureContentNode.data.domTargetId}
                command="--toggle"
                className={triggerClassName}
                isEditor={false}
                mentionItems={emptyMentionItems}
                refetchMentionitems={emptyRefetch}
              >
                {'children' in buttonNode &&
                  buttonNode.children?.map((childNode: ComponentTreeNode) =>
                    renderComponentNode(stripLinksFromTreeNode(childNode)),
                  )}
              </Button>
            );
          }

          return (
            <Disclosure {...treeNode.data} id={treeNode.id}>
              {triggerElement}
              {renderComponentNode(disclosureContentNode)}
            </Disclosure>
          );
        }

        case FlexiblePageTreeNodeType.DisclosureContent: {
          return (
            <DisclosureContent {...treeNode.data} key={treeNode.id} id={treeNode.id}>
              {'children' in treeNode && treeNode.children?.map((childNode) => renderComponentNode(childNode))}
            </DisclosureContent>
          );
        }

        case FlexiblePageTreeNodeType.CustomComponent: {
          const split = splitCustomComponentData(treeNode.data as Record<string, unknown>);
          return (
            <CustomComponent
              key={treeNode.id}
              customComponentId={split.customComponentId}
              customComponentSourceHash={split.customComponentSourceHash}
              className={split.className}
              authorProps={split.authorProps}
            />
          );
        }

        default: {
          console.warn(`Unknown component type: ${treeNode.data.type}`);
          return null;
        }
      }
    };

    const renderPageNode = (pageTreeNode: FlexiblePageTreeNode): React.ReactNode => {
      const isSlideDeck = isSlideDeckRenderMode(pageTreeNode.data.renderMode);
      const renderSlidesInline = isSlideDeck && isPdfExport;

      const renderSectionNode = (sectionNode: SectionTreeNode, forceRenderAsSlide = false): React.ReactNode => {
        return (
          <Section
            id={sectionNode.id}
            key={sectionNode.id}
            {...sectionNode.data}
            staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
            isEditor={isEditor}
            renderAsSlide={renderSlidesInline || forceRenderAsSlide}
          >
            {sectionNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
          </Section>
        );
      };

      const sectionChildren = pageTreeNode.children?.map((childNode) => renderSectionNode(childNode));
      // The thumbnail shows only slide one. `renderAsSlide` marks the section a `data-slide-active`
      // `group/slide` so the deck's entrance animations resolve to their final (visible) state —
      // without it the slide renders fully transparent.
      const firstSectionNode = pageTreeNode.children?.[0];
      const firstSlide = firstSectionNode ? renderSectionNode(firstSectionNode, true) : null;

      return (
        <Page
          treeNode={pageTreeNode}
          skipFontLoading={skipFontLoading}
          staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
        >
          {children}
          {/* Freeze CSS transitions/animations and hide scrollbar gutters so neither bleeds into the
              export screenshots — a static capture needs a deterministic frame. (Custom-component
              entrance animations are JS/SSR, handled separately at the SSR boundary, not by this CSS.) */}
          {isPdfExport && (
            <style>{`
              [data-flexible-page-root] *,
              [data-flexible-page-root] *::before,
              [data-flexible-page-root] *::after {
                transition: none !important;
                animation: none !important;
              }
              html, body, *, *::before, *::after {
                scrollbar-width: none !important;
                -ms-overflow-style: none !important;
              }
              html::-webkit-scrollbar,
              body::-webkit-scrollbar,
              *::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
              }
            `}</style>
          )}
          {isSlideDeck && isThumbnail ? (
            firstSlide ? (
              <SlideFrame index={0} staticScale>
                {firstSlide}
              </SlideFrame>
            ) : null
          ) : isSlideDeck && !renderSlidesInline ? (
            <SlideDeckHarness isEditor={isEditor}>{sectionChildren}</SlideDeckHarness>
          ) : (
            sectionChildren
          )}
        </Page>
      );
    };

    return <>{renderPageNode(componentTree)}</>;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.componentTree === nextProps.componentTree &&
      prevProps.overrideVariables === nextProps.overrideVariables &&
      prevProps.staticAssetsBaseURL === nextProps.staticAssetsBaseURL &&
      prevProps.renderStage === nextProps.renderStage &&
      prevProps.children === nextProps.children &&
      prevProps.skipFontLoading === nextProps.skipFontLoading &&
      prevProps.isEditor === nextProps.isEditor &&
      prevProps.isPdfExport === nextProps.isPdfExport &&
      prevProps.isThumbnail === nextProps.isThumbnail
    );
  },
);

ReadonlyComponentRenderer.displayName = 'ReadonlyComponentRenderer';
